import { SERVICE_BASE_URLS } from "../global/constants";
import { IExposure, IPortCapacity, IPreflight } from "../types/api/istio.type";
import api from "./api";

const ROOT = `${SERVICE_BASE_URLS.DB_MANAGER}/istio/db-gateway`;

const getPortCapacity = () => api.get<IPortCapacity>(`${ROOT}/ports`);

/**
 * Can an environment be exposed under this name right now?
 *
 * envId is omitted on the create path, where the environment does not exist yet; the
 * already-exposed check is then skipped and the answer is about capacity and the name.
 */
const preflight = (envId?: number, name?: string) => {
  const params = new URLSearchParams();
  if (envId != null) params.set("env_id", String(envId));
  if (name) params.set("name", name);
  const query = params.toString();
  return api.get<IPreflight>(`${ROOT}/preflight${query ? `?${query}` : ""}`);
};

const listExposures = () => api.get<IExposure[]>(`${ROOT}/exposures`);

const getExposure = (envId: number) => api.get<IExposure>(`${ROOT}/exposures/${envId}`);

const createExposure = (envId: number, name: string | undefined, tenantId: number) =>
  api.post<IExposure>(
    `${ROOT}/exposures`,
    { env_id: envId, name: name || undefined },
    { headers: { "tenant-id": String(tenantId) } }
  );

const deleteExposure = (envId: number, tenantId: number) =>
  api.delete(`${ROOT}/exposures/${envId}`, {
    headers: { "tenant-id": String(tenantId) },
  });

/**
 * Pulls the structured `detail` the API returns.
 *
 * `api.ts` has a response interceptor that rejects with `error.response.data` rather than the
 * AxiosError, so what arrives here is usually the API body already — both shapes are handled.
 */
export const exposureErrorDetail = (error: any): { code?: string; message?: string; [k: string]: any } => {
  const body = error?.response?.data ?? error;
  const detail = body?.detail ?? body;

  if (Array.isArray(detail)) {
    // FastAPI request validation: name the field, or "Field required" alone says nothing.
    const message = detail
      .map((d: any) => {
        const field = Array.isArray(d?.loc) ? d.loc[d.loc.length - 1] : undefined;
        return field && d?.msg ? `${field}: ${d.msg}` : d?.msg;
      })
      .filter(Boolean)
      .join("; ");
    return { code: "validation_error", message: message || "Request validation failed" };
  }
  if (detail && typeof detail === "object") return detail;
  if (typeof detail === "string") return { message: detail };
  return { message: error?.message || "Unexpected error" };
};

const IstioService = {
  getPortCapacity,
  preflight,
  listExposures,
  getExposure,
  createExposure,
  deleteExposure,
};

export default IstioService;
